import { motion } from "framer-motion";
import { WORK, TIMELINE, IMAGES } from "@/data/site";

export const OurWork = () => {
  return (
    <section
      id="work"
      className="relative z-10 bg-[#FDFBF7] py-24 lg:py-32"
      data-testid="work-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-16">
          <div className="lg:col-span-8">
            <p className="text-xs tracking-[0.3em] uppercase font-medium text-[#E07A5F] mb-6">
              Our Work
            </p>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#2B2D42] leading-tight tracking-tight">
              We advocate for healing, not just saving.
            </h2>
            <p className="mt-6 text-lg text-[#4A4E69] leading-relaxed max-w-2xl">
              Our mission is simple: to protect, support, and speak up for animals
              in need — especially those who have been abused, abandoned, or
              forgotten.
            </p>
          </div>
          <div className="lg:col-span-4">
            <div className="arch-frame aspect-[4/5] max-w-[240px] ml-auto shadow-[0_20px_60px_rgba(43,45,66,0.15)]">
              <img
                src={IMAGES.work}
                alt="Volunteer with a rescue dog"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {WORK.map((item, i) => (
            <motion.div
              key={item.n}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              className="group relative overflow-hidden rounded-3xl border border-[#E5DCC5] bg-white p-8 transition-all hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(224,122,95,0.12)]"
              data-testid={`work-card-${item.n}`}
            >
              <span className="font-display text-[7rem] leading-none font-black text-[#F4EBD9] absolute -top-4 -right-2 select-none transition-colors group-hover:text-[#F2CC8F]/50">
                {item.n}
              </span>
              <div className="relative">
                <h3 className="font-display text-2xl font-bold text-[#2B2D42] mb-3">
                  {item.title}
                </h3>
                <p className="text-[#4A4E69] leading-relaxed">{item.text}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-24" data-testid="work-timeline">
          <p className="text-xs tracking-[0.3em] uppercase font-medium text-[#E07A5F] mb-3 text-center">
            Our Journey
          </p>
          <h3 className="font-display text-3xl sm:text-4xl font-bold text-[#2B2D42] text-center mb-14">
            Milestones along the way.
          </h3>

          <div className="relative max-w-3xl mx-auto">
            <span
              className="absolute left-[7px] sm:left-1/2 top-2 bottom-2 w-px bg-[#E5DCC5] sm:-translate-x-1/2"
              aria-hidden="true"
            />
            <div className="space-y-12">
              {TIMELINE.map((item, i) => (
                <motion.div
                  key={item.date}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className={`relative pl-10 sm:pl-0 sm:w-1/2 ${
                    i % 2 === 0
                      ? "sm:pr-12 sm:text-right"
                      : "sm:ml-auto sm:pl-12"
                  }`}
                  data-testid={`timeline-item-${i}`}
                >
                  <span
                    className={`absolute top-1.5 h-3.5 w-3.5 rounded-full bg-[#E07A5F] ring-4 ring-[#FDFBF7] left-0 sm:left-auto ${
                      i % 2 === 0
                        ? "sm:right-0 sm:translate-x-[calc(3rem-6px)]"
                        : "sm:left-0 sm:-translate-x-[calc(3rem-8px)]"
                    }`}
                    aria-hidden="true"
                  />
                  <p className="text-sm font-semibold uppercase tracking-wide text-[#E07A5F]">
                    {item.date}
                  </p>
                  <h4 className="mt-1 font-display text-xl sm:text-2xl font-bold text-[#2B2D42]">
                    {item.title}
                  </h4>
                  <p className="mt-2 text-sm text-[#4A4E69] leading-relaxed">
                    {item.text}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="mt-16 font-display text-2xl sm:text-3xl italic text-center text-[#2B2D42] max-w-3xl mx-auto leading-snug"
        >
          Every tail matters. Every pawprint leaves a mark. Join us in creating a
          kinder world — one rescue at a time.
        </motion.p>
      </div>
    </section>
  );
};
